<template>
    <v-dialog
        v-model="dialogModel"
        width="900"
        max-width="95%"
        @click:outside.prevent="closeDialog"
    >
        <v-card>
            <v-card-title class="text-h6 text-md-h5 grey lighten-2">
                <v-row>
                    <v-col cols="10">
                        Passport and CPD Upload
                    </v-col>
                    <v-col cols="2" class="text-right">
                        <v-btn icon @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text class="py-5" style="max-height: 75vh; overflow-y: auto;">
                <v-form ref="passportCpdForm" lazy-validation>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.firstName"
                                placeholder="First Name"
                                outlined
                                dense
                                :rules="firstNameRules"
                                hint="First Name"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-end">
                            <v-text-field
                                v-model="form.lastName"
                                placeholder="Last Name"
                                outlined
                                dense
                                :rules="lastNameRules"
                                hint="Last Name"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <drag-drop-file-uploader
                        v-model="passportDocuments"
                        :accept="fileAcceptScannedDocuments"
                        title="File Upload. Passport/Photo ID Scan"
                        subTitle="Upload your passport/photo ID scan. Please ensure your scan is well lit and readable, with all corners of your ID visible and aligned to the edges of the scan. If you do not have access to a scanner, you may use your phone camera. If you cannot upload for any reason, please contact recruitment for further guidance."
                        hint="Please make sure all details are clear and readable"
                        :error="submitted && !passportDocuments.length"
                    />

                    <drag-drop-file-uploader
                        v-model="dentalQualificationDocuments"
                        :accept="fileAcceptScannedDocuments"
                        title="File Upload. Dental Qualification/Other"
                        subTitle="It is important that all Dental Nurses, Therapists, Hygienists and Dentists upload their Dental Qualification Certificates. Failure to do so without a viable reason could result in your offer of employment being revoked. For staff who are not from a dental background, please attach any qualifications relevant to your role."
                        hint="Please make sure all details are clear and readable"
                        :error="submitted && !dentalQualificationDocuments.length"
                    />

                    <drag-drop-file-uploader
                        v-model="cpdDocuments"
                        :accept="fileAcceptScannedDocuments"
                        title="File Upload. CPD"
                        subTitle="Please Upload all CPD Certificates obtained within the past 2 years."
                        hint="Please make sure all details are clear and readable"
                        :error="submitted && !cpdDocuments.length"
                    />

                    <div class="mt-5 pt-5" style="border-top:1px solid #e0e0e0">
                        <div class="d-flex justify-end">
                            <v-btn color="primary" :loading="submitLoading" @click="onSubmit">
                                Submit
                            </v-btn>
                        </div>
                    </div>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import DragDropFileUploader from '@/components/DragDropFileUploader.vue'
    import { FILE_ACCEPT_SCANNED_DOCUMENTS } from '@/common/constants'
    import validationRules from '@/common/validationRules'
    import { mapActions } from 'vuex'

    const emptyForm = () => ({
        firstName: '',
        lastName: ''
    })

    export default {
        name: 'DentistProfileFormDialog',
        components: {
            DragDropFileUploader
        },
        props: ['isOpen', 'programme'],
        data: () => ({
            form: emptyForm(),
            submitLoading: false,
            passportDocuments: [],
            dentalQualificationDocuments: [],
            cpdDocuments: [],
            submitted: false,
            fileAcceptScannedDocuments: FILE_ACCEPT_SCANNED_DOCUMENTS,

            requiredRules: validationRules.required,
            firstNameRules: validationRules.firstName,
            lastNameRules: validationRules.lastName
        }),
        computed: {
            dialogModel: {
                get () {
                    return this.isOpen
                },
                set (value) {
                    this.$emit('update:isOpen', value)
                }
            },
            isAllDocumentsUploaded () {
                return this.passportDocuments.length > 0 && this.dentalQualificationDocuments.length > 0 && this.cpdDocuments.length > 0
            }
        },
        methods: {
            ...mapActions([
             'showAlert',
             'uploadPassportCpdForm'
            ]),
            buildPayload () {
                return {
                    transitionProgrammeCourseId: this.programme && this.programme.id ? this.programme.id : 0,
                    firstName: this.form.firstName,
                    lastName: this.form.lastName,
                    formType: 'PassportAndCpdUpload'
                }
            },
            resetForm () {
                this.form = emptyForm()
                this.passportDocuments = []
                this.dentalQualificationDocuments = []
                this.cpdDocuments = []
                this.submitted = false
            },
            closeDialog () {
                this.resetForm()
                this.$emit('update:isOpen', false)
            },
            async onSubmit () {
                const formRef = this.$refs.passportCpdForm
                this.submitted = true
                if (!formRef.validate() || !this.isAllDocumentsUploaded) {
                    this.showAlert({
                        text: 'Fill all the required fields please',
                        type: 'error'
                    })
                    return
                }

                this.submitLoading = true
                try {
                    const payload = this.buildPayload()
                    await this.uploadPassportCpdForm(payload)
                    this.$emit('saved-with-additional-files', [...this.passportDocuments, ...this.dentalQualificationDocuments, ...this.cpdDocuments])
                    this.closeDialog()
                    if (formRef && formRef.resetValidation) formRef.resetValidation()
                } catch (e) {
                    this.showAlert({
                        text: e.message || 'Error saving form',
                        type: 'error'
                    })
                } finally {
                    this.submitLoading = false
                }
            }
        }
    }
</script>
